// PAINEL-1.2 — Painel unificado para GERENTE, DIRETOR, DIRETOR GERAL e ADMIN.
// Reúne em uma única tela, nessa ordem:
//   1) Título "Olá, {nome}" + "Painel do {papel} · {período}" + seletor de período
//   2) Faixa de atividades pendentes (ReminderBanner)
//   3) Cartão principal (anel % cota real, faturado, selo cota batida/falta,
//      barra, linha pequena "meta interna da equipe", "FATURAMENTO SANKHYA ·
//      ATUALIZADO ÀS hh:mm" no canto)
//   4) 6 indicadores de conquista (vindos de /painel/conquista)
//   5) Cartões da equipe (gerente→vendedores; diretor→gerentes; DG→diretores),
//      ordenados por % de atingimento (sem cota por último)
// Admin usa visão "Empresa" (lista de diretores) — mesmo backend do PAINEL-1.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LabelList, ComposedChart, Line, ReferenceLine, CartesianGrid } from "recharts";
import api from "@/lib/api";
import useFetch from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import { DashboardSkeleton, ErrorState, Avatar } from "@/components/common";
import { toast } from "sonner";
import { brl, brlShort, num, pct } from "@/lib/format";
import {
  Target, Bell, X, UserPlus, KanbanSquare, PauseCircle, Trophy, XCircle, Percent,
  ArrowLeft, ChevronRight, Home, Info,
} from "@/components/icons";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const MESES_PT = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];

function CoresRaw(cor) {
  switch (cor) {
    case "verde":    return { ring: "#10B981", bg: "bg-emerald-50", bar: "bg-emerald-500", text: "text-emerald-600" };
    case "amarelo":  return { ring: "#F59E0B", bg: "bg-amber-50",   bar: "bg-amber-500",   text: "text-amber-600" };
    case "vermelho": return { ring: "#EF4444", bg: "bg-rose-50",    bar: "bg-rose-500",    text: "text-rose-600" };
    default:         return { ring: "#94A3B8", bg: "bg-slate-100",  bar: "bg-slate-400",   text: "text-slate-500" };
  }
}

/** Faixa de atividades pendentes (reaproveita o endpoint /agenda).
 *  Vale só para a Home do próprio usuário (sem drill-down). */
function ReminderBanner({ hidden }) {
  const navigate = useNavigate();
  const [agenda, setAgenda] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("mpires_reminder_dismiss") === new Date().toDateString());
  useEffect(() => {
    if (hidden) { setAgenda(null); return; }
    api.get("/agenda").then((r) => setAgenda(r.data)).catch(() => {});
  }, [hidden]);
  if (dismissed || hidden || !agenda) return null;
  const atrasadas = agenda.counts?.atrasadas || 0;
  const hoje = agenda.counts?.hoje || 0;
  if (atrasadas + hoje === 0) return null;
  const close = () => { localStorage.setItem("mpires_reminder_dismiss", new Date().toDateString()); setDismissed(true); };
  const resumo = `${atrasadas > 0 ? (atrasadas === 1 ? "1 atrasada" : `${atrasadas} atrasadas`) : ""}${atrasadas > 0 && hoje > 0 ? " · " : ""}${hoje > 0 ? `${hoje} para hoje` : ""}`;
  return (
    <>
      <button
        onClick={() => navigate("/atividades")}
        data-testid="painel-reminder-mobile"
        className="md:hidden mb-4 w-full rounded-lg bg-navy text-white px-3 py-2 flex items-center justify-between gap-2 text-xs font-medium"
      >
        <span className="flex items-center gap-2 min-w-0 truncate">
          <Bell className="w-3.5 h-3.5 shrink-0" />
          <span className="truncate">{resumo || "Atividades pendentes"}</span>
        </span>
        <span className="font-semibold text-brand-soft shrink-0 flex items-center gap-1">Ver agenda <ChevronRight className="w-3.5 h-3.5" /></span>
      </button>
      <div className="hidden md:flex mb-4 rounded-xl bg-navy text-white p-4 items-center gap-4 relative overflow-hidden" data-testid="painel-reminder">
        <div className="absolute right-0 top-0 w-24 h-24 rounded-full bg-brand/30 blur-2xl" />
        <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center shrink-0"><Bell className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0 relative">
          <div className="font-semibold text-sm">Você tem atividades pendentes</div>
          <div className="text-xs text-white/70">{resumo}</div>
        </div>
        <button onClick={() => navigate("/atividades")} className="text-xs font-semibold bg-brand hover:bg-brand-hover px-3 py-2 rounded-lg transition-colors relative" data-testid="painel-reminder-open">Ver agenda</button>
        <button onClick={close} className="p-1.5 rounded-lg hover:bg-white/10 relative" data-testid="painel-reminder-dismiss"><X className="w-4 h-4" /></button>
      </div>
    </>
  );
}

/** Cartão principal: anel com % da cota real + faturado × cota real + selo
 *  Cota batida / Falta + barra + linha pequena "meta interna" + hora Sankhya. */
function CartaoPrincipal({ totais, periodo, atualizadoEmSankhya }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEditCotaMPires = user?.role === "admin" || user?.role === "diretor_geral";
  const cor = CoresRaw(totais?.cor || "cinza");
  const sem = totais?.sem_cota_real;
  const p = sem ? 0 : Math.min(100, totais?.atingimento_pct || 0);
  const ring = [{ value: p }, { value: Math.max(0, 100 - p) }];
  const horaSankhya = (() => {
    if (!atualizadoEmSankhya) return null;
    try {
      const d = new Date(atualizadoEmSankhya);
      if (Number.isNaN(d.getTime())) return null;
      return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    } catch { return null; }
  })();
  const batida = !sem && (totais?.atingimento_pct || 0) >= 100;
  return (
    <div className="rounded-2xl overflow-hidden shadow-[0_6px_24px_rgb(0,138,176,0.15)] bg-gradient-to-br from-navy to-brand text-white" data-testid="painel-cartao-principal">
      <div className="p-5 md:p-6">
        <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 sm:justify-between">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 opacity-80" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] opacity-80" data-testid="painel-cota-label">
              {(totais?.cota_label || "COTA REAL").toUpperCase()} · {periodo?.label?.toUpperCase() || ""}
            </span>
          </div>
          <span className="text-[10px] uppercase tracking-wider opacity-70 basis-full sm:basis-auto sm:ml-auto" data-testid="painel-sankhya-ts">
            Faturamento Sankhya{horaSankhya ? ` · atualizado às ${horaSankhya}` : ""}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative w-24 h-24 shrink-0" aria-hidden>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={ring} dataKey="value" innerRadius={32} outerRadius={44} startAngle={90} endAngle={-270} stroke="none" isAnimationActive={false}>
                  <Cell fill={sem ? "rgba(255,255,255,0.6)" : "#FFFFFF"} />
                  <Cell fill="rgba(255,255,255,0.18)" />
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-xl font-extrabold tabular-nums" data-testid="painel-pct">
                {sem ? "—" : `${Math.round(totais?.atingimento_pct || 0)}%`}
              </span>
              <span className="text-[9px] uppercase tracking-wider opacity-70">atingido</span>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            {sem ? (
              // PAINEL-1.7 (item 5) — mesmo sem cota cadastrada, mostrar o
              // faturado grande normalmente. Troca o % pela mensagem.
              <>
                <div className="text-2xl md:text-3xl font-bold leading-tight break-words" data-testid="painel-faturado">
                  {brl(totais?.faturado)}
                </div>
                <div className="text-xs opacity-80 mt-0.5">faturado</div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {canEditCotaMPires ? (
                    // PAINEL-1.8 (item 4) — admin/diretor geral: cadastrar
                    // diretamente, sem pedir para "outro diretor geral".
                    <button
                      type="button"
                      onClick={() => navigate("/equipe/cotas-lote?aba=mpires")}
                      data-testid="painel-aviso-sem-cota-mpires"
                      className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full bg-white/20 hover:bg-white/30 text-white px-2.5 py-1 transition-colors"
                    >
                      Cota da MPires não cadastrada · lançar em Equipe → Lançar cotas → Cota da MPires
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full bg-white/20 text-white px-2.5 py-1" data-testid="painel-aviso-sem-cota">
                      Cota real não cadastrada · peça ao diretor geral para cadastrar
                    </span>
                  )}
                </div>
                {totais?.cota_equipe_periodo ? (
                  <div className="mt-2 text-[11px] opacity-70" data-testid="painel-meta-interna">
                    {totais?.role === "empresa" ? "Distribuído aos diretores" : "Meta interna da equipe"}: {brl(totais.cota_equipe_periodo)}
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <div className="text-2xl md:text-3xl font-bold leading-tight break-words" data-testid="painel-faturado">
                  {brl(totais?.faturado)}
                </div>
                <div className="text-xs opacity-80 mt-0.5" data-testid="painel-faturado-sub">
                  faturado de {brl(totais?.cota_real_periodo)} de cota real
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {batida ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full bg-emerald-400/90 text-emerald-950 px-2.5 py-1" data-testid="painel-selo-batida">
                      ✓ Cota batida{(totais?.acima || 0) > 0 ? ` · ${brl(totais.acima)} acima` : ""}
                    </span>
                  ) : (
                    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2.5 py-1 ${
                      (totais?.atingimento_pct || 0) >= 80 ? "bg-amber-300/90 text-amber-950" : "bg-rose-300/90 text-rose-950"
                    }`} data-testid="painel-selo-falta">
                      Falta {brl(totais?.falta)}
                    </span>
                  )}
                </div>
                <div className="h-2 bg-white/20 rounded-full mt-2.5 overflow-hidden">
                  <div className="h-full bg-white rounded-full transition-all duration-700 shadow-[0_0_12px_rgba(255,255,255,0.6)]" style={{ width: `${p}%` }} />
                </div>
                {totais?.cota_equipe_periodo ? (
                  <div className="mt-2 text-[11px] opacity-70" data-testid="painel-meta-interna">
                    {totais?.role === "empresa" ? "Distribuído aos diretores" : "Meta interna da equipe"}: {brl(totais.cota_equipe_periodo)}
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** PAINEL-1.4 / UI-1 (itens 3 e 4) — "Atingimento da cota" com visual
 *  diferente no celular (lista) e no computador (gráfico horizontal).
 *  Carteira própria não entra (não tem cota). */
/* eslint-disable react/no-unstable-nested-components */
function GraficoAtingimento({ linhas, titulo, onOpen, onOpenSeller }) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 640);
  useEffect(() => {
    const onR = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);
  const dadosRaw = (linhas || [])
    .filter((l) => l.role !== "carteira_propria")
    .map((l) => ({
      id: l.id, nome: l.nome, nivel: l.nivel, role: l.role,
      pct: l.sem_cota_real ? null : Math.min(120, Math.max(0, l.atingimento_pct || 0)),
      pctReal: l.sem_cota_real ? null : (l.atingimento_pct || 0),
      cor: l.cor, drillable: l.drillable, sem_cota_real: l.sem_cota_real,
      faturado: l.faturado, cota: l.cota_real_periodo,
    }));
  const dados = [...dadosRaw].sort((a, b) => {
    if (a.sem_cota_real && !b.sem_cota_real) return 1;
    if (!a.sem_cota_real && b.sem_cota_real) return -1;
    if (a.sem_cota_real && b.sem_cota_real) return a.nome.localeCompare(b.nome);
    return (b.pct || 0) - (a.pct || 0);
  });
  const altura = Math.max(240, dados.length * 46);
  const corBarra = (cor) => ({ verde: "#10B981", amarelo: "#F59E0B", vermelho: "#EF4444", cinza: "#CBD5E1" }[cor] || "#CBD5E1");
  const classePct = (cor) => ({ verde: "text-emerald-600", amarelo: "text-amber-600", vermelho: "text-rose-600", cinza: "text-slate-400" }[cor] || "text-slate-500");

  const abrirLinha = (d) => {
    if (!d) return;
    if (d.role === "seller") { onOpenSeller?.(d.id); return; }
    if (d.drillable) onOpen?.(d.id);
  };

  const CustomTick = ({ x, y, payload }) => {
    const d = dados.find((r) => r.nome === payload.value);
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={-6} y={0} dy={4} textAnchor="end" fontSize={12} fill="#334155">{payload.value}</text>
        <text x={-6} y={14} dy={4} textAnchor="end" fontSize={10} fill="#94A3B8">
          {d?.sem_cota_real ? "Sem cota cadastrada" : `${brl(d?.faturado)} de ${brl(d?.cota)}`}
        </text>
      </g>
    );
  };
  const Tip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div className="rounded-lg bg-white border border-black/10 shadow-md px-3 py-2 text-xs">
        <div className="font-semibold text-navy">{d.nome}</div>
        {d.sem_cota_real ? (
          <div className="text-slate-500 mt-1">Sem cota cadastrada</div>
        ) : (
          <>
            <div className="text-slate-600 mt-1">Atingimento: <b className="tabular-nums">{(d.pctReal || 0).toFixed(1).replace(".", ",")}%</b></div>
            <div className="text-slate-600">Faturado: <b className="tabular-nums">{brl(d.faturado)}</b></div>
            <div className="text-slate-600">Cota real: <b className="tabular-nums">{brl(d.cota)}</b></div>
          </>
        )}
      </div>
    );
  };
  const handleClick = (data) => abrirLinha(data);
  return (
    <div className="bg-card rounded-2xl border border-black/5 p-4 shadow-[0_2px_10px_rgb(0,0,0,0.03)]" data-testid="painel-grafico-atingimento">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{titulo}</div>
        <div className="flex items-center gap-3 text-[11px] text-slate-500" data-testid="painel-legenda-cores">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500" /> ≥100% ✓</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-500" /> 80–99% !</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-rose-500" /> &lt;80% ▼</span>
        </div>
      </div>
      {isMobile ? (
        <ul className="divide-y divide-slate-100" data-testid="painel-atingimento-lista">
          {dados.length === 0 && (
            <li className="py-6 text-center text-sm text-muted-foreground">Sem dados.</li>
          )}
          {dados.map((d) => {
            const pct = d.sem_cota_real ? null : (d.pctReal || 0);
            const larguraBarra = d.sem_cota_real ? 0 : Math.min(120, Math.max(0, pct));
            const temCota = !d.sem_cota_real;
            return (
              <li
                key={d.id}
                onClick={() => abrirLinha(d)}
                className="py-3 cursor-pointer active:bg-slate-50 transition-colors"
                data-testid={`atingimento-item-${d.id}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1 text-sm font-medium text-navy truncate">{d.nome}</div>
                  <div className={`text-xl font-bold tabular-nums whitespace-nowrap ${classePct(d.cor)}`}>
                    {temCota ? `${Math.round(pct)}%` : "—"}
                  </div>
                </div>
                <div className="relative mt-2 h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                  {temCota && (
                    <div
                      className="absolute inset-y-0 left-0 rounded-full transition-all"
                      style={{ width: `${(larguraBarra / 120) * 100}%`, background: corBarra(d.cor) }}
                    />
                  )}
                  {/* Traço marcando 100% (cota). */}
                  <div className="absolute inset-y-0" style={{ left: `${(100 / 120) * 100}%`, width: 2, background: "#64748B" }} />
                </div>
                <div className="text-[11px] text-muted-foreground mt-1 tabular-nums whitespace-nowrap overflow-hidden text-ellipsis" title={temCota ? `${brl(d.faturado)} de ${brl(d.cota)}` : "Sem cota cadastrada"}>
                  {temCota ? (<>{brlShort(d.faturado)} de {brlShort(d.cota)}</>) : "Sem cota cadastrada"}
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <div style={{ width: "100%", height: altura }}>
          <ResponsiveContainer>
            {/* UI-1 (item 3) — margem top maior para o rótulo "cota" não cortar. */}
            <BarChart layout="vertical" data={dados} margin={{ top: 22, right: 56, bottom: 8, left: 140 }}>
              <CartesianGrid stroke="#EEF2F5" horizontal={false} />
              <XAxis type="number" domain={[0, 120]} ticks={[0, 20, 40, 60, 80, 100, 120]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="nome" tick={<CustomTick />} axisLine={false} tickLine={false} width={140} interval={0} />
              <Tooltip content={<Tip />} />
              <ReferenceLine x={100} stroke="#64748B" strokeDasharray="4 4" label={{ value: "cota", position: "top", fill: "#64748B", fontSize: 10, dy: -4 }} />
              <Bar dataKey="pct" radius={[0, 6, 6, 0]} onClick={handleClick} cursor="pointer" isAnimationActive={false}>
                {dados.map((d, i) => <Cell key={i} fill={corBarra(d.cor)} />)}
                <LabelList dataKey="pctReal" position="right" formatter={(v) => v == null ? "—" : `${Math.round(v)}%`} style={{ fontSize: 11, fill: "#334155", fontWeight: 600 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
/* eslint-enable react/no-unstable-nested-components */

/** PAINEL-1.4 / UI-1 (item 5) — "Faturado × cota real · últimos 12/6 meses"
 *  Card claro no mesmo estilo da tela Faturamento. Legenda com cores por %,
 *  linha dourada tracejada com pontos para a cota real, mês atual destacado,
 *  números do mês atual embaixo e tooltip detalhado. */
/* eslint-disable react/no-unstable-nested-components */
function GraficoHistorico12m({ scope, escopoLabel }) {
  const [serie, setSerie] = useState(null);
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 640);
  useEffect(() => {
    const onR = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);
  useEffect(() => {
    const params = scope ? `?scope=${scope}` : "";
    api.get(`/paineis/lideranca/historico-12m${params}`).then((r) => setSerie(r.data?.serie || [])).catch(() => setSerie([]));
  }, [scope]);
  if (!serie) return null;
  const dados = isMobile ? serie.slice(-6) : serie;
  if (dados.length === 0) return null;

  // UI-1 (item 5) — cor da barra em função do % atingido do mês.
  const corPorPct = (d) => {
    if (!d.cota_real || d.cota_real <= 0) return "#CBD5E1";
    const p = (d.faturado / d.cota_real) * 100;
    if (p >= 100) return "#10B981";
    if (p >= 80) return "#F59E0B";
    return "#EF4444";
  };
  const pctMes = (d) => (d.cota_real && d.cota_real > 0) ? (d.faturado / d.cota_real) * 100 : null;

  // Y-axis ticks: múltiplos redondos de 500k em "mi" sem repetir.
  const maxV = dados.reduce((m, d) => Math.max(m, d.faturado || 0, d.cota_real || 0), 0);
  const step = maxV >= 2_000_000 ? 500_000 : maxV >= 1_000_000 ? 250_000 : maxV >= 500_000 ? 125_000 : 50_000;
  const topoY = Math.ceil((maxV * 1.15) / step) * step;
  const yTicks = [];
  for (let v = 0; v <= topoY; v += step) yTicks.push(v);
  const fmtMi = (v) => {
    if (v === 0) return "0";
    if (v >= 1_000_000) return (v / 1_000_000).toLocaleString("pt-BR", { minimumFractionDigits: v % 1_000_000 === 0 ? 0 : 1, maximumFractionDigits: 2 });
    return (v / 1_000_000).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 2 });
  };

  const Tip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    const p = pctMes(d);
    return (
      <div className="rounded-lg bg-white border border-black/10 shadow-md px-3 py-2 text-xs">
        <div className="font-semibold text-navy">{label}</div>
        <div className="text-slate-600 mt-1">Faturado: <b className="tabular-nums">{brl(d.faturado)}</b></div>
        <div className="text-slate-600">Cota real: <b className="tabular-nums">{d.cota_real == null ? "—" : brl(d.cota_real)}</b></div>
        {p != null && <div className="text-slate-600">% atingido: <b className="tabular-nums">{p.toFixed(1).replace(".", ",")}%</b></div>}
      </div>
    );
  };
  const RotuloPct = ({ x, y, width, index }) => {
    const d = dados[index];
    const p = pctMes(d);
    if (p == null) return null;
    return (
      <text x={x + width / 2} y={y - 6} fill="#334155" fontSize={11} fontWeight={700} textAnchor="middle">
        {Math.round(p)}%
      </text>
    );
  };
  const BordaAtual = ({ x, y, width, height, index }) => {
    const d = dados[index];
    if (!d?.atual) return null;
    return <rect x={x - 1} y={y - 1} width={width + 2} height={height + 2} fill="none" stroke="#005A75" strokeWidth={2} rx={6} ry={6} />;
  };
  const atual = dados.find((d) => d.atual);
  const pctAtual = atual ? pctMes(atual) : null;

  const DOURADO = "#B7790F";
  return (
    <div className="bg-card rounded-2xl border border-black/5 p-4 shadow-[0_2px_10px_rgb(0,0,0,0.03)]" data-testid="painel-grafico-12m">
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
        Faturamento Sankhya{escopoLabel ? ` · ${escopoLabel}` : ""}
      </div>
      <div className="text-navy font-extrabold text-lg md:text-xl mt-0.5" style={{ fontFamily: "Barlow, system-ui, sans-serif" }}>Faturado × cota real</div>
      <div className="text-xs text-slate-500 mb-3">Últimos {isMobile ? 6 : 12} meses · % = faturado ÷ cota real</div>

      {/* Legenda. */}
      <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-[11px] text-slate-600 mb-2">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm bg-emerald-500" /> bateu a cota</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm bg-amber-500" /> 80–99%</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm bg-rose-500" /> abaixo de 80%</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-5 border-t-2 border-dashed" style={{ borderColor: DOURADO }} /> cota real</span>
      </div>

      <div style={{ width: "100%", height: isMobile ? 220 : 260, position: "relative" }}>
        <div style={{ position: "absolute", top: 2, right: 6, fontSize: 10, color: "#7C8C99" }}>R$ mi</div>
        <ResponsiveContainer>
          <ComposedChart data={dados} margin={{ top: 24, right: 12, bottom: 5, left: 2 }}>
            <CartesianGrid stroke="#EEF2F5" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} interval={0} />
            <YAxis
              tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false}
              ticks={yTicks} tickFormatter={fmtMi} domain={[0, topoY]} width={40}
            />
            <Tooltip content={<Tip />} cursor={{ fill: "#F1F5F9" }} />
            <Bar dataKey="faturado" radius={[6, 6, 0, 0]} isAnimationActive={false}>
              {dados.map((d, i) => <Cell key={i} fill={corPorPct(d)} />)}
              <LabelList dataKey="faturado" content={RotuloPct} />
              <LabelList dataKey="faturado" content={BordaAtual} />
            </Bar>
            <Line
              type="monotone" dataKey="cota_real" stroke={DOURADO} strokeWidth={2} strokeDasharray="5 4"
              dot={{ r: 3, fill: DOURADO }} isAnimationActive={false} connectNulls
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Rótulo "mês atual" embaixo do mês destacado — texto simples e centrado. */}
      {atual && (
        <div className="text-center text-[10px] text-slate-500 mt-1" data-testid="painel-12m-marcador-atual">mês atual: {atual.label}</div>
      )}

      {/* Resumo do mês atual: 3 números. */}
      {atual && (
        <div className="mt-3 grid grid-cols-3 gap-2 text-center" data-testid="painel-12m-resumo-atual">
          <div className="rounded-lg bg-slate-50 p-2 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Faturado</div>
            <div className="text-sm font-bold text-navy tabular-nums whitespace-nowrap overflow-hidden text-ellipsis" title={brl(atual.faturado)}>{brlShort(atual.faturado)}</div>
          </div>
          <div className="rounded-lg bg-slate-50 p-2 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Cota real</div>
            <div className="text-sm font-bold text-navy tabular-nums whitespace-nowrap overflow-hidden text-ellipsis" title={atual.cota_real == null ? "—" : brl(atual.cota_real)}>
              {atual.cota_real == null ? "—" : brlShort(atual.cota_real)}
            </div>
          </div>
          <div className="rounded-lg bg-slate-50 p-2 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">% atingido</div>
            <div className="text-sm font-bold tabular-nums whitespace-nowrap" style={{ color: pctAtual == null ? "#64748B" : pctAtual >= 100 ? "#10B981" : pctAtual >= 80 ? "#F59E0B" : "#EF4444" }}>
              {pctAtual == null ? "—" : `${pctAtual.toFixed(1).replace(".", ",")}%`}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
/* eslint-enable react/no-unstable-nested-components */



/** Cartão compacto de KPI de conquista. Clicável (lista) e com (i) opcional. */
function KpiCard({ icon: Icon, label, value, sub, tone = "navy", onClick, onInfo, testId, subMuted }) {
  const toneCls = tone === "ok" ? "text-ok" : tone === "warn" ? "text-warn" : tone === "bad" ? "text-bad" : tone === "muted" ? "text-slate-400" : tone === "brand" ? "text-brand" : "text-navy";
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      onClick={onClick}
      data-testid={testId}
      className={`text-left bg-card rounded-2xl border border-black/5 p-4 shadow-[0_2px_10px_rgb(0,0,0,0.03)] ${onClick ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-[0_10px_28px_rgb(0,0,0,0.07)] hover:border-brand/30 transition-all" : ""}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
          {label}
          {onInfo && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); onInfo(); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); onInfo(); } }}
              className="inline-flex items-center justify-center w-4 h-4 rounded-full text-muted-foreground hover:text-navy"
              data-testid={testId ? `${testId}-info` : "kpi-info"}
              aria-label="O que significa este número?"
            >
              <Info className="w-3.5 h-3.5" />
            </span>
          )}
        </span>
        <Icon className={`w-4 h-4 ${toneCls}`} />
      </div>
      <div className={`text-2xl font-bold mt-2 ${toneCls}`}>{value}</div>
      {sub && (
        <div className={`text-xs mt-1 ${subMuted ? "text-slate-400" : "text-muted-foreground"}`}>{sub}</div>
      )}
    </Comp>
  );
}

/** Modal reutilizável para listar negociações (ganhos / perdidos). */
function ListaDealsModal({ open, onOpenChange, titulo, itens, mostrarMotivo }) {
  const navigate = useNavigate();
  const total = (itens || []).reduce((s, i) => s + (Number(i.valor) || 0), 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-muted-foreground -mt-2 mb-2">
          {(itens || []).length} negociação{(itens || []).length === 1 ? "" : "ões"} · {brl(total)}
        </div>
        {(itens || []).length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Sem negociações no período.</div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100">
            {itens.map((i) => (
              <button
                key={i.id}
                onClick={() => { onOpenChange(false); if (i.cliente_id) navigate(`/clientes/${i.cliente_id}`); }}
                data-testid={`deal-row-${i.id}`}
                className="w-full text-left py-3 px-1 flex items-start gap-3 hover:bg-slate-50 rounded-lg"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-navy truncate">{i.cliente_nome || i.titulo || "—"}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {i.owner_name ? `${i.owner_name} · ` : ""}
                    {i.data ? new Date(i.data).toLocaleDateString("pt-BR") : ""}
                    {mostrarMotivo && i.motivo ? ` · ${i.motivo}` : ""}
                  </div>
                </div>
                <div className="text-sm font-semibold text-navy tabular-nums shrink-0">{brl(i.valor)}</div>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Modal explicativo da "Taxa de ganho" + listas resumidas. */
function TaxaGanhoInfoModal({ open, onOpenChange, conquista }) {
  const tg = conquista?.taxa_ganho || {};
  const ganhos = (conquista?.ganhos?.items || []).slice(0, 20);
  const perdidos = (conquista?.perdidos?.items || []).slice(0, 20);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Taxa de ganho — como funciona</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-700">
          Das negociações que <b>terminaram no período</b> (ganhas + perdidas), quantas foram ganhas.
          Não conta negociações ainda abertas nem o faturamento do Sankhya.
        </p>
        <div className="mt-2 text-sm">
          <b>Ganhas</b>: {tg.ganhos_count ?? 0} · <b>Encerradas</b>: {tg.encerrados_count ?? 0}
          {tg.amostra_pequena && <span className="ml-2 text-slate-400">(amostra pequena)</span>}
        </div>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[48vh] overflow-y-auto">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-ok mb-1">Ganhas</div>
            {ganhos.length === 0 ? (
              <div className="text-xs text-muted-foreground">Nenhuma</div>
            ) : ganhos.map((i) => (
              <div key={i.id} className="text-xs py-1.5 border-b border-slate-100">
                <div className="font-medium text-navy truncate">{i.cliente_nome || i.titulo || "—"}</div>
                <div className="text-muted-foreground">{brl(i.valor)} · {i.owner_name || "—"}</div>
              </div>
            ))}
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-bad mb-1">Perdidas</div>
            {perdidos.length === 0 ? (
              <div className="text-xs text-muted-foreground">Nenhuma</div>
            ) : perdidos.map((i) => (
              <div key={i.id} className="text-xs py-1.5 border-b border-slate-100">
                <div className="font-medium text-navy truncate">{i.cliente_nome || i.titulo || "—"}</div>
                <div className="text-muted-foreground">{brl(i.valor)} · {i.owner_name || "—"} · {i.motivo || "—"}</div>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function PainelUnificado() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const now = new Date();

  // Estado do seletor de período (controla cartão principal e cartões filhos).
  const [periodoTipo, setPeriodoTipo] = useState(sp.get("periodo_tipo") || "mes");
  const [mes, setMes] = useState(Number(sp.get("mes") || (now.getMonth() + 1)));
  const [tri, setTri] = useState(Number(sp.get("tri") || Math.floor(now.getMonth() / 3) + 1));
  const [year, setYear] = useState(Number(sp.get("year") || now.getFullYear()));
  const scope = sp.get("scope") || "";
  const [painel, setPainel] = useState(null);
  const [loadingP, setLoadingP] = useState(true);

  const load = useCallback(async () => {
    setLoadingP(true);
    try {
      const params = { periodo_tipo: periodoTipo, year };
      if (periodoTipo === "mes") params.mes = mes;
      if (periodoTipo === "tri") params.tri = tri;
      if (scope) params.scope = scope;
      const r = await api.get("/paineis/lideranca", { params });
      setPainel(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar painel");
    } finally { setLoadingP(false); }
  }, [periodoTipo, mes, tri, year, scope]);
  useEffect(() => { load(); }, [load]);

  const syncURL = (patch) => {
    const p = new URLSearchParams(sp);
    const next = { periodo_tipo: periodoTipo, mes, tri, year, scope, ...patch };
    p.set("periodo_tipo", next.periodo_tipo);
    p.set("year", String(next.year));
    if (next.periodo_tipo === "mes") { p.set("mes", String(next.mes)); p.delete("tri"); }
    else if (next.periodo_tipo === "tri") { p.set("tri", String(next.tri)); p.delete("mes"); }
    else { p.delete("mes"); p.delete("tri"); }
    if (next.scope) p.set("scope", next.scope); else p.delete("scope");
    setSp(p);
  };

  // Indicadores de conquista: /painel/conquista aceita owner_id como escopo.
  // Quando o usuário está "mergulhado" (scope != ""), passamos esse owner_id.
  const conquistaOwner = scope || "";
  const conquistaUrl = conquistaOwner ? `/painel/conquista?owner_id=${conquistaOwner}` : "/painel/conquista";
  const { data: conquista } = useFetch(conquistaUrl);

  const anos = useMemo(() => [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1], [now]);

  // PAINEL-1.3 — estado dos modais do drill-down dos 6 indicadores.
  const [modalGanhos, setModalGanhos] = useState(false);
  const [modalPerdidos, setModalPerdidos] = useState(false);
  const [modalTaxa, setModalTaxa] = useState(false);
  const criadoMes = useMemo(() => {
    // "Prospects novos" é filtrado por mês de criação. Usa o mês do período
    // atual quando aplicável; no modo trimestre/ano usa o mês de hoje.
    const y = year;
    const mm = periodoTipo === "mes" ? mes : (now.getMonth() + 1);
    return `${y}-${String(mm).padStart(2, "0")}`;
  }, [year, periodoTipo, mes, now]);
  // Target de scope para passar na navegação ("vendedor atual" quando scope != "").
  const scopeFiltroQS = scope ? `&scope_id=${scope}` : "";

  if (loadingP && !painel) return <DashboardSkeleton />;
  if (!painel) return <ErrorState onRetry={load} />;

  const { periodo, crumbs, totais, linhas = [], atualizado_em_sankhya } = painel;
  // Ordena filhos: maior % primeiro; sem cota por último; empate por nome.
  const linhasOrdenadas = [...linhas].sort((a, b) => {
    if (a.sem_cota_real && !b.sem_cota_real) return 1;
    if (!a.sem_cota_real && b.sem_cota_real) return -1;
    if (a.sem_cota_real && b.sem_cota_real) return (a.nome || "").localeCompare(b.nome || "");
    return (b.atingimento_pct || 0) - (a.atingimento_pct || 0);
  });
  const abrir = (uid) => syncURL({ scope: uid || "" });
  const voltar = () => {
    const prev = (crumbs || [])[(crumbs || []).length - 2];
    abrir(prev?.id || "");
  };
  const papelLabel = {
    admin: "Admin", diretor_geral: "Diretor Geral", director: "Diretor", manager: "Gerente",
  }[user?.role] || "Gestor";

  return (
    <div className="pb-24 lg:pb-0" data-testid="painel-unificado">
      {/* UI-1 (item 1) — breadcrumbs só aparecem após drill-down (scope != "").
       *  Admin / Diretor Geral: começam em "MPires".
       *  Diretor / Gerente: começam no próprio nome (sem "Empresa"). */}
      {scope && (() => {
        const r = user?.role;
        let visiveis = crumbs || [];
        if (r === "admin" || r === "diretor_geral") {
          visiveis = visiveis.map((c, i) => i === 0 ? { ...c, nome: "MPires" } : c);
        } else if (r === "director" || r === "manager") {
          visiveis = visiveis.slice(1);
        }
        if (visiveis.length === 0) return null;
        return (
          <div className="flex items-center flex-wrap gap-1 text-sm text-slate-500 mb-3" data-testid="painel-crumbs">
            {visiveis.map((c, i) => (
              <span key={i} className="flex items-center gap-1">
                {i > 0 && <ChevronRight className="w-3.5 h-3.5" />}
                {i === visiveis.length - 1 ? (
                  <span className="font-medium text-slate-900">{c.nome}</span>
                ) : (
                  <button onClick={() => abrir(c.id || "")} className="hover:text-navy flex items-center gap-1" data-testid={`crumb-${i}`}>
                    {i === 0 && (r === "admin" || r === "diretor_geral") && <Home className="w-3.5 h-3.5" />}
                    {c.nome}
                  </button>
                )}
              </span>
            ))}
          </div>
        );
      })()}

      {/* Cabeçalho + seletor de período */}
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-navy">
            {scope ? totais?.nome : `Olá, ${user?.name?.split(" ")[0] || ""}`}
          </h1>
          <p className="text-sm text-muted-foreground mt-1" data-testid="painel-subtitulo">
            Painel do {scope ? (totais?.nivel || "").toLowerCase() : papelLabel.toLowerCase()} · {periodo?.label}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="painel-periodo">
          <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden">
            {["mes","tri","ano"].map((t) => (
              <button
                key={t}
                onClick={() => { setPeriodoTipo(t); syncURL({ periodo_tipo: t }); }}
                data-testid={`periodo-${t}`}
                className={`px-3 py-1.5 text-sm font-medium ${periodoTipo === t ? "bg-navy text-white" : "bg-white text-slate-700"}`}
              >
                {t === "mes" ? "Mês" : t === "tri" ? "Trimestre" : "Ano"}
              </button>
            ))}
          </div>
          {periodoTipo === "mes" && (
            <select value={mes} onChange={(e) => { setMes(Number(e.target.value)); syncURL({ mes: Number(e.target.value) }); }} data-testid="sel-mes"
              className="h-9 px-2 border border-slate-300 rounded-lg text-sm bg-white">
              {MESES_PT.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
            </select>
          )}
          {periodoTipo === "tri" && (
            <select value={tri} onChange={(e) => { setTri(Number(e.target.value)); syncURL({ tri: Number(e.target.value) }); }} data-testid="sel-tri"
              className="h-9 px-2 border border-slate-300 rounded-lg text-sm bg-white">
              {[1,2,3,4].map((q) => <option key={q} value={q}>T{q}</option>)}
            </select>
          )}
          <select value={year} onChange={(e) => { setYear(Number(e.target.value)); syncURL({ year: Number(e.target.value) }); }} data-testid="sel-ano"
            className="h-9 px-2 border border-slate-300 rounded-lg text-sm bg-white">
            {anos.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          {scope && (
            <button onClick={voltar} data-testid="painel-voltar" className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-input text-sm font-medium hover:bg-slate-50">
              <ArrowLeft className="w-4 h-4" /> Voltar
            </button>
          )}
        </div>
      </div>

      {/* Faixa de atividades pendentes (home do próprio usuário). */}
      <ReminderBanner hidden={!!scope} />

      {/* Cartão principal (anel + % cota real + faturado × cota + selo + meta interna). */}
      <div className="mb-5"><CartaoPrincipal totais={totais} periodo={periodo} atualizadoEmSankhya={atualizado_em_sankhya} /></div>

      {/* Indicadores de conquista — mesmas 6 caixinhas do painel atual, 2/linha no mobile. */}
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Indicadores de conquista</div>
      </div>
      <div className="text-[11px] text-slate-500 mb-3" data-testid="painel-legenda-conquista">
        Números do funil registrados no MPires Connect (não é o faturamento do Sankhya).
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="painel-conquista-kpis">
        {conquista ? (
          <>
            <KpiCard
              icon={UserPlus} label="Prospects novos"
              value={num(conquista.prospects_novos?.count || 0)} tone="brand"
              onClick={() => navigate(`/clientes?criado_mes=${criadoMes}${scopeFiltroQS}`)}
              testId="kpi-prospects-novos"
            />
            <KpiCard
              icon={KanbanSquare} label="Pipeline aberto"
              value={num(conquista.pipeline_total?.count || 0)}
              sub={brl(conquista.pipeline_total?.valor || 0)}
              onClick={() => navigate(`/pipeline${scope ? `?scope_id=${scope}` : ""}`)}
              testId="kpi-pipeline-aberto"
            />
            <KpiCard
              icon={PauseCircle} label="Parados 7d+"
              value={num(conquista.parados_7d?.count || 0)}
              sub={brl(conquista.parados_7d?.valor || 0)}
              tone={(conquista.parados_7d?.count || 0) > 0 ? "warn" : "navy"}
              onClick={() => navigate(`/foco/paradas${scope ? `?scope_id=${scope}` : ""}`)}
              testId="kpi-parados-7d"
            />
            <KpiCard
              icon={Trophy} label="Ganhos no mês"
              value={num(conquista.ganhos?.count || 0)}
              sub={brl(conquista.ganhos?.valor || 0)} tone="ok"
              onClick={() => setModalGanhos(true)}
              testId="kpi-ganhos"
            />
            <KpiCard
              icon={XCircle} label="Perdidos no mês"
              value={num(conquista.perdidos?.count || 0)}
              sub={brl(conquista.perdidos?.valor || 0)} tone="bad"
              onClick={() => setModalPerdidos(true)}
              testId="kpi-perdidos"
            />
            {/* PAINEL-1.3 — "Taxa de ganho" (antes "Taxa de conversão") com subtítulo
                "N ganhos de M encerradas" e cinza quando amostra < 10. */}
            {(() => {
              const tg = conquista.taxa_ganho || {};
              const amostraPequena = !!tg.amostra_pequena;
              const valorPct = tg.pct == null ? "—" : pct(tg.pct, 1);
              const sub = `${tg.ganhos_count || 0} ganhos de ${tg.encerrados_count || 0} negociações encerradas no período`;
              const tone = amostraPequena ? "muted" : ((tg.pct || 0) >= 30 ? "ok" : "navy");
              return (
                <KpiCard
                  icon={Percent} label="Taxa de ganho"
                  value={valorPct}
                  sub={amostraPequena ? `${sub} · amostra pequena` : sub}
                  subMuted={amostraPequena}
                  tone={tone}
                  onClick={() => setModalTaxa(true)}
                  onInfo={() => setModalTaxa(true)}
                  testId="kpi-taxa-ganho"
                />
              );
            })()}
          </>
        ) : null}
      </div>

      {/* PAINEL-1.4 — gráficos no lugar dos cartões de vendedor/gerente/diretor. */}
      {linhasOrdenadas.length > 0 && (
        <div className="grid grid-cols-1 gap-4 mb-6" data-testid="painel-equipe-graficos">
          <GraficoAtingimento
            titulo={`Atingimento da cota por ${(painel.escopo?.nivel_filhos || "equipe").toLowerCase()}`}
            linhas={linhasOrdenadas}
            onOpen={(id) => abrir(id)}
            onOpenSeller={(id) => navigate(`/clientes?scope_id=${id}`)}
          />
          <GraficoHistorico12m scope={scope} escopoLabel={scope ? (totais?.nome || "") : (user?.role === "admin" || user?.role === "diretor_geral" ? "MPires" : (user?.name?.split(" ")[0] || ""))} />
        </div>
      )}

      {/* PAINEL-1.3 — Drill-down dos indicadores clicáveis. */}
      <ListaDealsModal
        open={modalGanhos}
        onOpenChange={setModalGanhos}
        titulo={`Ganhos — ${periodo?.label || ""}`}
        itens={conquista?.ganhos?.items || []}
      />
      <ListaDealsModal
        open={modalPerdidos}
        onOpenChange={setModalPerdidos}
        titulo={`Perdidos — ${periodo?.label || ""}`}
        itens={conquista?.perdidos?.items || []}
        mostrarMotivo
      />
      <TaxaGanhoInfoModal
        open={modalTaxa}
        onOpenChange={setModalTaxa}
        conquista={conquista}
      />
    </div>
  );
}
